export const SHEEN_FIRST_DELAY_MS = 3000;
export const SHEEN_INTERVAL_MS = 12500;
export const SHEEN_TRIGGER_QUEUE_WINDOW_MS = 100;
// How far other movement, such as the quick-play breathe, keeps from a sheen.
export const SHEEN_CLEARANCE_MS = 1000;


/**
 * Decides when a sheen runs: first after a delay, then again a fixed
 * interval after the last one finished, and on a trigger, such as the
 * pointer entering or leaving, when none is running.
 */
export class SheenScheduler {
    private readonly durationMs: number;
    private readonly onStart: () => void;
    private timer: ReturnType<typeof setTimeout> | null = null;
    private runningSinceMs: number | null = null;
    private lastEndMs: number | null = null;
    private nextStartMs: number | null = null;
    private queued = false;

    constructor(durationMs: number, onStart: () => void) {
        this.durationMs = durationMs;
        this.onStart = onStart;
    }

    begin(): void {
        this.schedule(this.startSheen, SHEEN_FIRST_DELAY_MS);
    }

    stop(): void {
        if (this.timer !== null)
            clearTimeout(this.timer);

        this.timer = null;
        this.nextStartMs = null;
    }

    /**
     * Whether something that runs for durationMs from now keeps
     * SHEEN_CLEARANCE_MS clear of every sheen, past and scheduled. A
     * trigger can still start a sheen at any moment.
     */
    isClearFor(durationMs: number): boolean {
        const nowMs = performance.now();
        if (this.runningSinceMs !== null)
            return false;
        if (this.lastEndMs !== null && nowMs - this.lastEndMs < SHEEN_CLEARANCE_MS)
            return false;

        return this.nextStartMs === null || this.nextStartMs - nowMs >= durationMs + SHEEN_CLEARANCE_MS;
    }

    trigger(): void {
        if (this.runningSinceMs === null) {
            this.startSheen();
            return;
        }

        const remainingMs = this.durationMs - (performance.now() - this.runningSinceMs);
        if (remainingMs <= SHEEN_TRIGGER_QUEUE_WINDOW_MS) {
            this.queued = true;
        }
    }

    private schedule(action: () => void, delayMs: number): void {
        this.stop();
        this.timer = setTimeout(action, delayMs);
        if (action === this.startSheen)
            this.nextStartMs = performance.now() + delayMs;
    }

    private readonly startSheen = (): void => {
        this.runningSinceMs = performance.now();
        this.nextStartMs = null;
        this.onStart();
        this.schedule(this.finishSheen, this.durationMs);
    };

    private readonly finishSheen = (): void => {
        this.runningSinceMs = null;
        this.lastEndMs = performance.now();
        if (this.queued) {
            this.queued = false;
            this.startSheen();
            return;
        }

        this.schedule(this.startSheen, SHEEN_INTERVAL_MS);
    };
}
